/**
 * AI CONTEXT
 * Purpose: normalizes enrolled + exception students into grouped attendance sections.
 * Handles: mapping statuses/session IDs and forwarding attendance mutations.
 * Used by: AttendanceView per-session detail block.
 * Refactor: extract exception mapping logic into pure utility for unit tests.
 */
import { orderBy, uniq } from "lodash"
import { AnimatePresence } from "motion/react"
import { useCallback, useMemo, useState } from "react"

import { classSessionAtoms } from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Log from "#classes/Log"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { useFirebaseContext } from "#contexts/FirebaseContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { AttendanceRowData } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus, invariant } from "#customTypes/types"
import { useSomeDocs } from "#hooks/jotai/useSubscription"
import { StudentsByExceptionType } from "#hooks/newHooks/useExceptionListData"
import { getSessionInfoFromId, isDefined } from "#utils/classHelpers"
import { isStudentProjectOnTrack } from "#utils/studentHelpers"
import { hasOpenTasks, isPresentStatus } from "#utils/taskHelpers"

import AttendanceArtworkDropHost from "./AttendanceArtworkDropHost"
import AttendanceViewStudentListSection from "./AttendanceViewStudentListSection"

const FIELDS: (keyof StudentsByExceptionType)[] = [
  "assistants",
  "makeups",
  "tryouts",
  "plannedAbsences",
]

const AttendanceViewStudentListContainer = ({
  notes,
  session,
  classGroup,
  studentsByExceptionType,
  studentsById,
}: {
  notes: string | undefined
  session: ClassSessionConstructor
  classGroup: CLASS_GROUPS
  studentsByExceptionType: StudentsByExceptionType
  studentsById: Record<Uuid, StudentConstructor>
}) => {
  const { border, altColor } = useThemeContext()

  const { onStudentAttendanceChange } = useFirebaseContext()
  const { markStudentPresent, clearPresentHighlight, setTaskLineOpen } =
    useAdminViewContext()

  // Files dropped on a row stage here until the upload modal confirms them.
  const [droppedArtwork, setDroppedArtwork] = useState<{
    student: StudentConstructor
    files: File[]
  } | null>(null)

  const handleMediaDrop = useCallback(
    (student: StudentConstructor, files: File[]) =>
      setDroppedArtwork({ student, files }),
    []
  )

  const missedSessionIds = useMemo(() => {
    const ids =
      studentsByExceptionType.makeups
        ?.filter(({ sessionId }) => sessionId === session.id)
        .map(
          ({ exceptionData }) =>
            exceptionData?.missedSessionId as Uuid | undefined
        )
        .filter(isDefined) ?? []
    return uniq(ids)
  }, [studentsByExceptionType.makeups, session.id])

  const missedSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    missedSessionIds
  )

  const projectIdBySessionId = useMemo(() => {
    const map: Partial<Record<Uuid, Uuid>> = {}
    missedSessions.forEach((missedSession) => {
      if (missedSession?.id && missedSession.projectId) {
        map[missedSession.id] = missedSession.projectId
      }
    })
    return map
  }, [missedSessions])

  const handleStatusChange = useCallback(
    (
      student: StudentConstructor,
      status: AttendanceStatus,
      sessionId: Uuid,
      projectId?: Uuid,
      attendanceField?: "attendance" | "assistantAttendance"
    ) => {
      const [classId, sessionIndex] = getSessionInfoFromId(sessionId)
      onStudentAttendanceChange(
        student,
        classId,
        sessionIndex,
        status,
        attendanceField,
        projectId
      )

      // Any status change resets the manual flag toggle: the line returns to
      // its automatic state (open while present), so marking present/tardy
      // auto-opens it even if someone closed it earlier.
      setTaskLineOpen(student.id, null)

      // Bridge to the task panel (JARVIS-69): arriving students with open
      // tasks get their card highlighted; clearing/absenting removes it.
      // No open tasks -> nothing recorded, so the panel is untouched.
      if (!isPresentStatus(status)) {
        clearPresentHighlight(student.id)
      } else if (hasOpenTasks(student.tasks)) {
        markStudentPresent(student.id)
      }
    },
    [
      onStudentAttendanceChange,
      markStudentPresent,
      clearPresentHighlight,
      setTaskLineOpen,
    ]
  )

  const rows = useMemo(() => {
    const result: Record<string, AttendanceRowData[]> = {
      enrolled: [],
      exceptions: [],
      assistants: [],
      makeups: [],
      tryouts: [],
      plannedAbsences: [],
    }

    const students = session?.studentIds
      ?.map((id: Uuid) => {
        const student = studentsById[id]

        // TODO: This happens in the render between receiving data and calculating output. Fix it!
        if (!student) return

        return student
      })
      .filter(isDefined)

    const orderedStudents = orderBy(students, "name", "asc")

    orderedStudents.forEach((student) => {
      const sessionId = session.id
      const [classId, sessionIndex] = getSessionInfoFromId(sessionId)
      const status = student?.attendance?.[classId]?.[sessionIndex]?.status

      if (!status) {
        console.error(
          "✅🔥 status not found for ",
          Log.student(student),
          `classId: ${classId}, sessionIndex: ${sessionIndex}`
        )
      }

      const row: AttendanceRowData = {
        student,
        status: status ?? ATTENDANCE_STATUSES.INVALID,
        sessionId,
        projectId: session.projectId,
      }

      if (!isStudentProjectOnTrack(student, session, status)) {
        result.exceptions.push(row)
      } else {
        result.enrolled.push(row)
      }
    })

    // Exceptions come out sorted
    FIELDS.forEach((field) => {
      const studentExceptions = studentsByExceptionType[field]

      if (!studentExceptions) return

      studentExceptions.forEach((studentException) => {
        const {
          student,
          sessionId: originalSessionId,
          exceptionData,
          exceptionType,
        } = studentException

        invariant(
          exceptionType == "assistant" || exceptionData != null,
          `Exception data is missing for ${Log.student(student)}`
        )

        if (!student || originalSessionId !== session.id) return

        const attendanceKey =
          field === "assistants" ? "assistantAttendance" : "attendance"

        let status: AttendanceStatus | undefined
        let finalSessionId = originalSessionId

        if (
          (field === "makeups" || field === "plannedAbsences") &&
          !!exceptionData &&
          "missedSessionId" in exceptionData
        ) {
          const missedSessionId = exceptionData.missedSessionId as Uuid
          const [missedClassId, missedSessionIndex] =
            getSessionInfoFromId(missedSessionId)

          status =
            student?.[attendanceKey]?.[missedClassId]?.[missedSessionIndex]
              ?.status
          finalSessionId = missedSessionId
        } else {
          const [classId, sessionIndex] = getSessionInfoFromId(session.id)
          status =
            student?.[attendanceKey]?.[classId]?.[sessionIndex]?.status ??
            ATTENDANCE_STATUSES.INVALID
        }

        invariant(
          !!status,
          `Status not found for ${Log.student(student)} in session ${session.id} for field ${field}
${field === "makeups" ? `missedSessionId: ${exceptionData?.missedSessionId} makeupId: ${exceptionData?.id}` : ""}`
        )

        result[field].push({
          student,
          status,
          sessionId: finalSessionId,
          projectId:
            finalSessionId === session.id
              ? session.projectId
              : projectIdBySessionId[finalSessionId],
        })
      })
    })
    return result
  }, [session, studentsByExceptionType, studentsById, projectIdBySessionId])

  return (
    <AnimateInFlex
      wait={500}
      animation={ANIMATION_BY_KEY.down}
      col
      style={{
        overflowY: "scroll",
        width: 350,
        height: "auto",
        backgroundColor: border,
        padding: "20px 0px",
        marginTop: -20,
        position: "relative",
        zIndex: 0,
        borderRadius: "0px 0px 4px 4px",
      }}
    >
      <AnimatePresence mode="popLayout">
        {notes ? (
          <AnimateInFlex
            animation={ANIMATION_BY_KEY.down}
            style={{
              fontSize: 12,
              backgroundColor: altColor,
              color: "white",
              padding: "14px 10px 4px",
              marginTop: -20,
              borderRadius: "0px 0px 4px 4px",
            }}
          >
            {notes}
          </AnimateInFlex>
        ) : null}
        <AttendanceViewStudentListSection
          key="tryouts"
          students={rows.tryouts}
          field="tryouts"
          classGroup={classGroup}
          session={session}
          onStatusChange={handleStatusChange}
          onMediaDrop={handleMediaDrop}
        />
        <AttendanceViewStudentListSection
          key="makeups"
          students={rows.makeups}
          field="makeups"
          classGroup={classGroup}
          session={session}
          onStatusChange={handleStatusChange}
          onMediaDrop={handleMediaDrop}
        />
        <AttendanceViewStudentListSection
          key="plannedAbsences"
          students={rows.plannedAbsences}
          field="plannedAbsences"
          classGroup={classGroup}
          session={session}
          onStatusChange={handleStatusChange}
          onMediaDrop={handleMediaDrop}
        />
        <AttendanceViewStudentListSection
          key="exceptions"
          students={rows.exceptions}
          field="exceptions"
          classGroup={classGroup}
          session={session}
          onStatusChange={handleStatusChange}
          onMediaDrop={handleMediaDrop}
        />
        <AttendanceViewStudentListSection
          key="enrolled"
          students={rows.enrolled}
          field="students"
          classGroup={classGroup}
          session={session}
          onStatusChange={handleStatusChange}
          onMediaDrop={handleMediaDrop}
        />
        <AttendanceViewStudentListSection
          key="assistants"
          students={rows.assistants}
          field="assistants"
          classGroup={classGroup}
          session={session}
          onStatusChange={(student, status, sessionId, projectId) =>
            handleStatusChange(
              student,
              status,
              sessionId,
              projectId,
              "assistantAttendance"
            )
          }
          onMediaDrop={handleMediaDrop}
        />
      </AnimatePresence>
      {droppedArtwork ? (
        <AttendanceArtworkDropHost
          student={droppedArtwork.student}
          files={droppedArtwork.files}
          sessionClassId={getSessionInfoFromId(session.id)[0]}
          onClose={() => setDroppedArtwork(null)}
        />
      ) : null}
    </AnimateInFlex>
  )
}

export default AttendanceViewStudentListContainer
